import { Component, OnDestroy, OnInit } from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BInquiry, BInteraction } from '../../data-model/interaction/interaction';
import { DetailHistoryEditComponent } from './detail-history-edit/detail-history-edit.component';
import { BInquirer, SingleInteractionService } from 'app/modules/data-model/data-model.module';
import { InteractionsService } from 'app/modules/data-model/interaction/interactions.service';
import {
  Config,
  ConfigItem,
  ConfigItemOption,
} from 'app/common/common/search-select/search-select.config';
import { HistoryFilters } from './detail-history-filters';
import { SelectInquiryComponent } from './detail-history-interaction/select-inquiry/select-inquiry.component';
import { AuthService } from 'app/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { HISTORY_DIALOG_MODES } from 'app/common/types';
import { PERM_ADMINISTRATE_SYSTEM } from 'app/common/common/constants/permissions';
import { Message } from 'app/common/common/message/message.component';
import { InquirersService } from 'app/modules/data-model/inquirer/inquirers.service';

@Component({
  selector: 'app-detail-history',
  templateUrl: './detail-history.component.html',
  styleUrls: ['./detail-history.component.scss'],
})
export class DetailHistoryComponent implements OnInit, OnDestroy {
  interaction: BInteraction;
  inquiry: BInquiry;

  visualize: string = 'interaction';
  hideAdd: boolean = false;
  interactionConfig: Config;

  selectedInteractionFilter: string[] = [];
  interactionHistory: InteractionsService.History = { history: [], inquirers: [] };
  isInteractionHistoryLoading: boolean = false;
  inquirerHistory: BInquirer[] = [];
  isInquirerHistoryLoading: boolean = false;
  loggedInUserIsSysAdmin: boolean = false;
  message: Message;

  private _mode: HISTORY_DIALOG_MODES = 'history';
  private subs: Subscription = new Subscription();

  constructor(
    translate: TranslateService,
    private modalService: MatDialog,
    private singleInteractionService: SingleInteractionService,
    private interactionsService: InteractionsService,
    private authService: AuthService,
    private inquirersService: InquirersService
  ) {
    const all = HistoryFilters.getAllFilters();
    const options = all.map((f) => new ConfigItemOption(translate.instant(f.name), f.value));
    const items = [ConfigItem.onlySearchAndOptions('Search', options)];
    this.interactionConfig = new Config(all, 'FILTER_BY', items);
  }

  ngOnInit(): void {
    this.subs.add(
      this.authService.permissions.subscribe((handler) => {
        this.loggedInUserIsSysAdmin = handler.has(PERM_ADMINISTRATE_SYSTEM);
      })
    );

    this.reloadInteractionHistory();
    this.loadInquirerHistory();
    this.subs.add(
      this.singleInteractionService.interaction.subscribe((interaction) => {
        this.interaction = interaction;
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  set mode(val: HISTORY_DIALOG_MODES) {
    this._mode = val;
    if (val === 'internal_comm') {
      this.selectedInteractionFilter = [val];
    }
  }

  get mode(): HISTORY_DIALOG_MODES {
    return this._mode;
  }

  openAddNote(): void {
    if (this.interaction.inquiries.length > 1) {
      const modalRef = this.modalService.open(SelectInquiryComponent);
      modalRef.componentInstance.interaction = this.interaction;
      modalRef.componentInstance.titleTranslationKey = 'SELECT_QUESTION_FOR_NOTE';
      modalRef.componentInstance.noSelectedEnquiryTranslationKey = 'ADD_NOTE_FOR_WHOLE_ENQUIRY';
      modalRef.afterClosed().subscribe((inquiry) => {
        this.addNote(inquiry);
      });
    } else {
      this.addNote();
    }
  }

  refreshInteraction(): void {
    this.singleInteractionService.refreshInteraction(this.interaction.id, true);
  }

  private addNote(inquiry?: BInquiry): void {
    const modalRef = this.modalService.open(DetailHistoryEditComponent);
    modalRef.componentInstance.interaction = this.interaction;
    modalRef.componentInstance.inquiry = inquiry;
    modalRef.componentInstance.isNote = true;
    this.subs.add(
      modalRef.componentInstance.historyChanged.subscribe(() => {
        this.reloadInteractionHistory();
        this.refreshInteraction();
      })
    );
  }

  private reloadInteractionHistory(): void {
    this.isInteractionHistoryLoading = true;
    this.subs.add(
      this.interactionsService
        .getHistory(this.interaction.pk(), this.mode === 'internal_comm')
        .pipe(finalize(() => (this.isInteractionHistoryLoading = false)))
        .subscribe({
          next: (resp) => (this.interactionHistory = resp),
          error: (error) => (this.message = Message.httpError('Load History', error)),
        })
    );
  }

  private loadInquirerHistory(): void {
    if (this.mode === 'internal_comm' || !this.interaction.inquirer?.id) {
      return;
    }
    this.isInquirerHistoryLoading = true;
    this.subs.add(
      this.inquirersService
        .getHistory(this.interaction.inquirer.pk())
        .pipe(finalize(() => (this.isInquirerHistoryLoading = false)))
        .subscribe({
          next: (historyData) => (this.inquirerHistory = historyData),
          error: (resp) => (this.message = Message.httpError('Load Enquirer History', resp)),
        })
    );
  }
}
